(function () {
  'use strict';

  const AUTOPLAY_MS = 3200;
  const RESISTANCE_SLIDE_HIDE_MS = 980;
  const DEFAULT_SLIDE_HIDE_MS = 260;
  const PREWARM_ROOT_MARGIN = 800;

  function prewarmMedia(container) {
    if (!container) return;

    container.querySelectorAll('img').forEach((image) => {
      image.loading = 'eager';
      image.decoding = 'async';
      if (typeof image.decode === 'function' && !image.complete) {
        image.decode().catch(() => {});
      }
    });

    container.querySelectorAll('video').forEach((video) => {
      if (!video.preload || video.preload === 'none') {
        video.preload = 'metadata';
      }
      if (video.readyState === 0) video.load();
    });
  }

  class HpgBandUspCarousel {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-hpg-usp-slide]'));
      this.dots = Array.from(root.querySelectorAll('[data-hpg-usp-slide-dot]'));
      this.previousButton = root.querySelector('[data-hpg-scroll-prev]');
      this.nextButton = root.querySelector('[data-hpg-scroll-next]');
      this.index = 0;
      this.timer = null;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.autoplay = root.dataset.hpgUspAutoplay !== 'false';
      this.resistanceMotionStage = root.classList.contains('hpg-band-usp__stage--resistance') ? root : null;
      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.swipeStart = null;
      this.prewarmObserver = null;
      this.pendingPrewarmIndex = 0;

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.setActive(Number(dot.dataset.hpgUspSlideDot || 0));
          this.restart();
        });
      });
      this.previousButton?.addEventListener('click', () => this.move(-1));
      this.nextButton?.addEventListener('click', () => this.move(1));

      root.addEventListener('mouseenter', () => this.stop());
      root.addEventListener('mouseleave', () => this.start());
      root.addEventListener('focusin', () => this.stop());
      root.addEventListener('focusout', () => this.start());
      this.setupMobileSwipe();

      this.setActive(0, true);
      this.start();
    }

    start() {
      if (!this.autoplay || this.reduceMotion || this.slides.length < 2 || this.timer) return;
      this.timer = window.setInterval(() => {
        this.setActive((this.index + 1) % this.slides.length);
      }, AUTOPLAY_MS);
    }

    stop() {
      if (!this.timer) return;
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }

    move(direction) {
      if (this.slides.length < 2) return;

      const nextIndex = (this.index + direction + this.slides.length) % this.slides.length;
      this.setActive(nextIndex);
      this.restart();
    }

    setActive(nextIndex, immediate) {
      if (nextIndex === this.index && !immediate) return;
      const previous = this.slides[this.index];
      const next = this.slides[nextIndex];
      if (!next) return;

      next.hidden = false;
      if (!immediate) next.offsetHeight;
      next.classList.add('is-active');

      if (previous && previous !== next) {
        previous.classList.remove('is-active');
        const hideDelay = this.resistanceMotionStage ? RESISTANCE_SLIDE_HIDE_MS : DEFAULT_SLIDE_HIDE_MS;
        window.setTimeout(() => {
          if (!previous.classList.contains('is-active')) previous.hidden = true;
        }, immediate ? 0 : hideDelay);
      }

      this.dots.forEach((dot, index) => {
        const active = index === nextIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.index = nextIndex;
      this.prewarmNearby(nextIndex);
      this.restartLayerMotion();
    }

    prewarmNearby(index) {
      if (!this.isNearPrewarmRange() && 'IntersectionObserver' in window) {
        this.observePrewarm(index);
        return;
      }

      this.disconnectPrewarmObserver();

      [index, index + 1].forEach((slideIndex) => {
        prewarmMedia(this.slides[slideIndex]);
      });
    }

    isNearPrewarmRange() {
      const rect = this.root.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      return rect.top < viewportHeight + PREWARM_ROOT_MARGIN && rect.bottom > -PREWARM_ROOT_MARGIN;
    }

    observePrewarm(index) {
      this.pendingPrewarmIndex = index;
      if (this.prewarmObserver) return;

      this.prewarmObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        const prewarmIndex = this.pendingPrewarmIndex;
        this.disconnectPrewarmObserver();
        this.prewarmNearby(prewarmIndex);
      }, {
        rootMargin: `${PREWARM_ROOT_MARGIN}px 0px`,
        threshold: 0,
      });

      this.prewarmObserver.observe(this.root);
    }

    disconnectPrewarmObserver() {
      if (!this.prewarmObserver) return;

      this.prewarmObserver.disconnect();
      this.prewarmObserver = null;
    }

    restartLayerMotion() {
      if (!this.resistanceMotionStage || !this.resistanceMotionStage.classList.contains('is-layer-motion-ready')) return;
      if (this.mobileQuery.matches) return;
      this.resistanceMotionStage.classList.remove('is-layer-motion-visible');
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.resistanceMotionStage.classList.add('is-layer-motion-visible');
        });
      });
    }

    setupMobileSwipe() {
      if (this.slides.length < 2) return;

      const start = (clientX, clientY, pointerId) => {
        if (!this.mobileQuery.matches) return;
        this.stop();
        this.swipeStart = { clientX, clientY, pointerId };
      };

      const finish = (clientX, clientY, pointerId) => {
        if (!this.swipeStart || this.swipeStart.pointerId !== pointerId) return;
        const deltaX = clientX - this.swipeStart.clientX;
        const deltaY = clientY - this.swipeStart.clientY;
        const threshold = Math.max(44, this.root.clientWidth * 0.12);
        this.swipeStart = null;

        if (Math.abs(deltaX) < threshold || Math.abs(deltaX) < Math.abs(deltaY) * 1.15) {
          this.restart();
          return;
        }

        const direction = deltaX < 0 ? 1 : -1;
        const nextIndex = (this.index + direction + this.slides.length) % this.slides.length;
        this.setActive(nextIndex);
        this.restart();
      };

      if (window.PointerEvent) {
        this.root.addEventListener('pointerdown', (event) => {
          if (event.button !== 0 || !event.isPrimary) return;
          start(event.clientX, event.clientY, event.pointerId);
        });

        this.root.addEventListener('pointerup', (event) => {
          finish(event.clientX, event.clientY, event.pointerId);
        });

        this.root.addEventListener('pointercancel', () => {
          this.swipeStart = null;
          this.restart();
        });

        return;
      }

      this.root.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;
        start(touch.clientX, touch.clientY, touch.identifier);
      }, { passive: true });

      this.root.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        if (!touch) return;
        finish(touch.clientX, touch.clientY, touch.identifier);
      }, { passive: true });
    }
  }

  function init() {
    document.querySelectorAll('[data-hpg-usp-carousel]').forEach((root) => {
      if (root.dataset.hpgUspInitialized === 'true') return;
      root.dataset.hpgUspInitialized = 'true';
      new HpgBandUspCarousel(root);
    });

    document.querySelectorAll('.hpg-band-usp__stage').forEach((stage) => {
      if (stage.dataset.hpgHotspotsInitialized === 'true') return;
      stage.dataset.hpgHotspotsInitialized = 'true';
      initHotspots(stage);
    });

    document.querySelectorAll('.hpg-band-usp__stage--resistance').forEach((stage) => {
      if (stage.dataset.hpgLayerMotionInitialized === 'true') return;
      stage.dataset.hpgLayerMotionInitialized = 'true';
      initResistanceLayerMotion(stage);
    });
  }

  function initHotspots(stage) {
    const hotspots = Array.from(stage.querySelectorAll('.hpg-band-usp__stack-hotspot, .hpg-band-usp__quality-hotspot'));
    if (!hotspots.length) return;

    const closeAll = (exceptCard) => {
      hotspots.forEach((hotspot) => {
        const card = hotspot.nextElementSibling;
        if (!card || card === exceptCard) return;
        card.classList.remove('is-open');
        hotspot.setAttribute('aria-expanded', 'false');
      });
    };

    hotspots.forEach((hotspot) => {
      const card = hotspot.nextElementSibling;
      if (!card) return;
      hotspot.setAttribute('aria-expanded', 'false');

      hotspot.addEventListener('click', (event) => {
        event.preventDefault();
        const willOpen = !card.classList.contains('is-open');
        closeAll(card);
        card.classList.toggle('is-open', willOpen);
        hotspot.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });

    });

    stage.addEventListener('mouseover', (event) => {
      const hotspot = event.target.closest('.hpg-band-usp__stack-hotspot, .hpg-band-usp__quality-hotspot');
      if (!hotspot || !stage.contains(hotspot)) return;

      closeAll(hotspot.nextElementSibling);
    });

    stage.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closeAll();
    });
  }

  function initResistanceLayerMotion(stage) {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      stage.classList.add('is-layer-motion-ready');
      stage.classList.add('is-layer-motion-visible');
      return;
    }

    let revealed = false;

    const reveal = () => {
      if (revealed) return;
      revealed = true;
      stage.classList.add('is-layer-motion-ready');
      window.requestAnimationFrame(() => {
        stage.classList.add('is-layer-motion-visible');
      });
    };

    stage.classList.add('is-layer-motion-ready');

    if (!('IntersectionObserver' in window)) {
      reveal();
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        reveal();
        observer.unobserve(stage);
      });
    }, { threshold: 0.35 });

    observer.observe(stage);

    const rect = stage.getBoundingClientRect();
    const visibleNow = rect.top < window.innerHeight * 0.82 && rect.bottom > window.innerHeight * 0.18;
    if (visibleNow) {
      window.requestAnimationFrame(reveal);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
